import { useEffect, useState, useRef } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Upload } from "lucide-react";

const FIELDS = [
    { key: "site_title", label: "Site Title" },
    { key: "tagline", label: "Tagline (under logo)" },
    { key: "season", label: "Current Season" },
    { key: "hero_headline", label: "Hero Headline" },
    { key: "hero_subline", label: "Hero Subline" },
    { key: "hero_blurb", label: "Hero Blurb", type: "textarea" },
    { key: "hero_image_url", label: "Hero Image", type: "image" },
    { key: "logo_url", label: "Logo Image (Optional, replaces default crown mark)", type: "image" },
    { key: "contact_email", label: "Contact Email" },
    { key: "contact_phone", label: "Contact Phone" },
    { key: "contact_address", label: "Address" },
    { key: "instagram_url", label: "Instagram URL" },
    { key: "twitter_url", label: "Twitter / X URL" },
    { key: "facebook_url", label: "Facebook URL" },
    { key: "youtube_url", label: "YouTube URL" },
];

export default function AdminSettings() {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/settings").then((r) => setS(r.data));
    }, []);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...s };
            delete payload.id;
            delete payload.created_at;
            const { data } = await api.put("/settings", payload);
            setS(data);
            toast.success("Settings saved");
        } catch (err) {
            toast.error(formatErr(err));
        } finally { setSaving(false); }
    };

    if (!s) return <div className="text-ink">Loading…</div>;

    return (
        <div data-testid="admin-settings">
            <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-2">Brand</div>
            <h1 className="h-display text-4xl mb-8">Site Settings</h1>
            <form onSubmit={save} className="space-y-5 max-w-3xl">
                {FIELDS.map((f) => (
                    <div key={f.key}>
                        <label className="text-[10px] tracking-widest uppercase text-ink font-bold">{f.label}</label>
                        {f.type === "textarea" ? (
                            <textarea rows={3} value={s[f.key] || ""} onChange={(e) => setS({ ...s, [f.key]: e.target.value })} className="w-full mt-2 px-4 py-3 text-sm" data-testid={`set-${f.key}`} />
                        ) : f.type === "image" ? (
                            <ImageField value={s[f.key]} onChange={(v) => setS({ ...s, [f.key]: v })} testid={`set-${f.key}`} />
                        ) : (
                            <input value={s[f.key] || ""} onChange={(e) => setS({ ...s, [f.key]: e.target.value })} className="w-full mt-2 px-4 py-3 text-sm" data-testid={`set-${f.key}`} />
                        )}
                    </div>
                ))}
                <button type="submit" disabled={saving} data-testid="settings-save" className="btn-gold px-7 py-4 text-xs disabled:opacity-60">
                    {saving ? "Saving…" : "Save Settings"}
                </button>
            </form>
        </div>
    );
}

function ImageField({ value, onChange, testid }) {
    const ref = useRef(null);
    const [up, setUp] = useState(false);

    const upload = async (file) => {
        if (!file) return;
        setUp(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(process.env.REACT_APP_BACKEND_URL + data.url);
            toast.success("Uploaded");
        } catch (err) { toast.error(formatErr(err)); }
        finally { setUp(false); }
    };

    return (
        <div className="mt-2" data-testid={testid}>
            <div className="flex gap-3 items-start">
                {value && <img src={value} alt="" className="w-24 h-24 object-cover border border-line" />}
                <div className="flex-1 space-y-2">
                    <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://… or upload" className="w-full px-4 py-3 text-sm" />
                    <div className="flex gap-2">
                        <button type="button" onClick={() => ref.current?.click()} disabled={up} className="btn-ghost-gold px-3 py-2 text-[10px] inline-flex items-center gap-2">
                            <Upload size={12} /> {up ? "Uploading…" : "Upload"}
                        </button>
                        {value && <button type="button" onClick={() => onChange("")} className="text-red-400 text-[10px] uppercase tracking-widest">Remove</button>}
                    </div>
                    <input type="file" ref={ref} hidden accept="image/*" onChange={(e) => upload(e.target.files?.[0])} />
                </div>
            </div>
        </div>
    );
}
